/**
 * Status bubble (top left) — collapsed to a status dot + short label; a tap
 * expands it to reveal the data source, flight count and region.
 */
export class StatusBar {
  constructor() {
    this.bar = document.getElementById('statusBar');
    this.statusDot = this.bar.querySelector('.status-dot');
    this.statusText = this.bar.querySelector('.status-text');
    this.statusProvider = document.getElementById('statusProvider');
    this.statusUpdated = document.getElementById('statusUpdated');
    this.flightCount = document.getElementById('flightCount');
    this.regionName = document.getElementById('regionName');

    // Standardmäßig eingeklappt (nur Punkt + Label) — Tippen zeigt die Details.
    // Klicks im aufgeklappten Dropdown sollen die Bubble nicht wieder schließen.
    this.details = document.getElementById('statusDetails');
    if (this.details) {
      this.details.addEventListener('click', (e) => e.stopPropagation());
    }
    this.bar.addEventListener('click', () => this.toggle());
    this.bar.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggle();
      }
    });
  }

  /**
   * Details ein-/ausklappen. Ohne Argument wird umgeschaltet.
   * @param {boolean} [force]
   */
  toggle(force) {
    const expanded = typeof force === 'boolean'
      ? force
      : !this.bar.classList.contains('expanded');
    this.bar.classList.toggle('expanded', expanded);
    this.bar.setAttribute('aria-expanded', String(expanded));
  }

  /**
   * Set data source status.
   * @param {Object} status - { state: 'live'|'fallback'|'demo'|'error',
   *   provider, index, total, error }
   *   index/total: Rang der aktiven Quelle in der Failover-Kette (0-basiert)
   *   und Gesamtzahl verfügbarer Quellen ("wie viel Backup").
   */
  setDataStatus({ state, provider, index = 0, total = 0, error }) {
    this.statusDot.classList.remove('connected', 'demo');

    // "adsb.fi · Backup 2/4" bzw. "adsb.lol · primäre Quelle"
    const backups = Math.max(total - 1, 0);
    const sourceLabel = (suffix) => provider
      ? `${provider} · ${suffix}`
      : suffix;

    switch (state) {
      case 'live':
        this.statusDot.classList.add('connected');
        this.statusText.textContent = 'Live';
        this.statusProvider.textContent = sourceLabel(
          backups ? `primär (${backups} Backup${backups === 1 ? '' : 's'})` : 'primär'
        );
        this.bar.title = `Live-ADS-B-Daten von ${provider}`;
        break;
      case 'fallback':
        this.statusDot.classList.add('connected');
        this.statusText.textContent = 'Live';
        this.statusProvider.textContent = sourceLabel(
          total ? `Backup ${index + 1}/${total}` : 'Backup'
        );
        this.bar.title = `Primärer Anbieter nicht erreichbar — nutze ${provider}`;
        break;
      case 'demo':
        this.statusDot.classList.add('demo');
        this.statusText.textContent = 'Demo';
        this.statusProvider.textContent = 'Simulierte Flüge';
        this.bar.title = error
          ? `${error}\nZeige simulierte Flüge`
          : 'Zeige simulierte Flugdaten';
        break;
      default:
        this.statusText.textContent = 'Offline';
        this.statusProvider.textContent = 'Keine Daten';
        this.bar.title = error || 'Datenanbieter antworten nicht';
    }
  }

  /**
   * Zeitstempel der letzten erfolgreichen Datenaktualisierung anzeigen.
   * @param {number} [timestamp] - ms seit Epoch; Standard: jetzt
   */
  setLastUpdate(timestamp = Date.now()) {
    if (!this.statusUpdated) return;
    this.statusUpdated.textContent = new Date(timestamp).toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  /**
   * Update flight count display
   * @param {number} count
   */
  updateFlightCount(count) {
    const text = count === 1 ? '1 Flugzeug' : `${count} Flugzeuge`;
    this.flightCount.textContent = text;
  }

  /**
   * Set region name
   * @param {string} region
   */
  setRegion(region) {
    this.regionName.textContent = region;
  }
}
